import React from 'react';
import { Plus, Trash2, Wand2, Settings, Download } from 'lucide-react';
import { Button, Text, Heading, AppSidebar, SIDEBAR_ITEMS } from '../design-system';

/**
 * /admin/design-system — Storybook-style demo route.
 * Documentation only. No business logic.
 */
export default function DesignSystemPage() {
  return (
    <div className="min-h-screen flex bg-[color:var(--color-surface)]">
      <AppSidebar />

      <main className="flex-1 overflow-y-auto p-8 max-w-[1100px] mx-auto" data-testid="design-system-page">
        <header className="mb-10">
          <Text variant="mono" tone="secondary">UI-1 — Phase 1</Text>
          <Heading level="display" className="mt-2">Design System</Heading>
          <Text variant="body" tone="secondary" className="mt-2 block">
            Foundation primitives. All app surfaces consume these. Updates here ripple everywhere.
          </Text>
        </header>

        <Section title="Buttons">
          <Subsection label="Variants">
            <Row>
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="tertiary">Tertiary</Button>
              <Button variant="destructive" icon={Trash2}>Delete</Button>
            </Row>
          </Subsection>

          <Subsection label="Sizes (md is default)">
            <Row>
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
            </Row>
          </Subsection>

          <Subsection label="Icons">
            <Row>
              <Button icon={Plus}>Add Project</Button>
              <Button variant="secondary" icon={Wand2}>Generate</Button>
              <Button variant="tertiary" icon={Settings}>Settings</Button>
              <Button trailingIcon={Download}>Export</Button>
            </Row>
          </Subsection>

          <Subsection label="States">
            <Row>
              <Button loading>Loading…</Button>
              <Button disabled>Disabled</Button>
              <Button variant="secondary" disabled>Disabled secondary</Button>
            </Row>
          </Subsection>
        </Section>

        <Section title="Typography">
          <div className="space-y-4">
            <Heading level="display">Display — 32px / 700</Heading>
            <Heading level="h1">H1 — 24px / 600</Heading>
            <Heading level="h2">H2 — 18px / 600</Heading>
            <Text variant="body">Body — 14px / 400. The quick brown fox jumps over the lazy dog.</Text>
            <Text variant="micro" tone="secondary" as="div">Micro — 12px / 500 secondary</Text>
            <Text variant="mono" tone="primary" as="div">MONO LABEL · 11PX · 600 · UPPERCASE</Text>
          </div>
        </Section>

        <Section title="Color Tokens">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['primary', 'var(--color-primary)'],
              ['primary-hover', 'var(--color-primary-hover)'],
              ['success', 'var(--color-success)'],
              ['warning', 'var(--color-warning)'],
              ['danger', 'var(--color-danger)'],
              ['featured', 'var(--color-featured)'],
              ['surface', 'var(--color-surface)'],
              ['surface-alt', 'var(--color-surface-alt)'],
            ].map(([name, val]) => (
              <div key={name} className="rounded-[var(--radius-md)] overflow-hidden border border-[color:var(--color-border)]">
                <div style={{ background: val, height: 56 }} />
                <div className="px-3 py-2 bg-[color:var(--color-surface-alt)]">
                  <Text variant="mono" tone="secondary" as="div">{name}</Text>
                  <Text variant="micro" tone="muted" as="div" className="font-mono">{val}</Text>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Sidebar (canonical)">
          <Text variant="body" tone="secondary" className="mb-3 block">
            Renders identically on every authenticated route. {SIDEBAR_ITEMS.length} items, no per-page variants.
          </Text>
          <ol className="list-decimal list-inside space-y-1 columns-2">
            {SIDEBAR_ITEMS.map((it) => (
              <li key={it.id} className="text-[14px] text-[color:var(--color-text-secondary)]">{it.label}</li>
            ))}
          </ol>
        </Section>
      </main>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mb-10">
    <Heading level="h1" className="mb-4 pb-2 border-b border-[color:var(--color-border)]">{title}</Heading>
    {children}
  </section>
);
const Subsection = ({ label, children }) => (
  <div className="mb-5">
    <Text variant="mono" tone="muted" as="div" className="mb-2">{label}</Text>
    {children}
  </div>
);
const Row = ({ children }) => <div className="flex flex-wrap items-center gap-3">{children}</div>;
